// @ts-strict-ignore

import type {
  BigQuerySegmentsConfig,
  CdiSegmentsConfig,
  DatabricksSegmentsConfig,
  RedshiftSegmentsConfig,
  SnowflakeSegmentsConfig,
  FabricSegmentsConfig,
} from "@dashboardApi/apis/integrationsDirectCdiSegmentsConfig";
import type { PartnerName } from "@technology-partners/cdi-import-sync-shared/shared/types";
import type { SyncDataType } from "@technology-partners/cdi-import-sync-shared/syncs/CdiSyncDataType";

import type {
  InitializeMapDataMap,
  InitializeMapDestinations,
  InitializeMapMappingRule,
  InitializeMapMappingSummary,
  InitializeMapSourceField,
} from "@dashboardApi/apis/appsAppGroupIdUserImportsImportIdInitializeMap/types";
import type { I18nT } from "@lib/dashboardI18n";
import sharedConstants from "@lib/shared_constants";

export type CdiImportStatus = "active" | "paused" | "draft";

export const syncTypeFriendlyName = (syncDataType: SyncDataType, t: I18nT<"cdi-import-sync">): string => {
  switch (syncDataType) {
    case "canvas":
      return t("sync-type-canvas", "Canvas Triggers");
    case "attributes":
      return t("sync-type-user-attributes", "User Attributes");
    case "catalog":
      return t("sync-type-catalog", "Catalog");
    case "deletes":
      return t("sync-type-delete-users", "Delete Users");
    case "events":
      return t("sync-type-custom-events", "Custom Events");
    case "purchases":
      return t("sync-type-purchase-events", "Purchase Events");
    case "accounts":
      return t("sync-type-accounts", "Accounts");
  }
};

export interface BaseImportSync {
  integration_id?: string;
  integration_name: string;
  integration_type: SyncDataType;
  integration_status?: CdiImportStatus;
  contact_emails: string;
  notification_preference?: NotificationPreference;
  schedule: string;
  api_key?: string;
  last_sync?: CdiDataSync;
  update_existing_only?: boolean;
  catalog_name?: string;
  canvas_id?: string;
  source_data_warehouse?: string;
}

export interface CDISeparateSourceSync extends BaseImportSync {
  source_data_warehouse: string;
  partner_name?: PartnerName;
  table_name?: string;
  sqs_url?: string;
  folder?: string;
  reader_behavior?: "standard" | "custom_sql";
  sql_query?: string;
}

export interface SnowflakeImportSync extends BaseImportSync {
  data_warehouse?: SnowflakeWarehouse;
  partner_name: typeof sharedConstants.DATALAKE.PARTNER_NAME.snowflake.name;
}

export interface RedshiftImportSync extends BaseImportSync {
  data_warehouse?: RedshiftWarehouse;
  partner_name: typeof sharedConstants.DATALAKE.PARTNER_NAME.redshift.name;
}

export interface BigQueryImportSync extends BaseImportSync {
  data_warehouse?: BigQueryWarehouse;
  partner_name: typeof sharedConstants.DATALAKE.PARTNER_NAME.bigquery.name;
}

export interface DatabricksImportSync extends BaseImportSync {
  data_warehouse?: DatabricksWarehouse;
  partner_name: typeof sharedConstants.DATALAKE.PARTNER_NAME.databricks.name;
}

export interface S3ImportSync extends BaseImportSync {
  data_warehouse?: S3Warehouse;
  partner_name: typeof sharedConstants.DATALAKE.PARTNER_NAME.s3.name;
}

export interface FabricImportSync extends BaseImportSync {
  data_warehouse?: FabricWarehouse;
  partner_name: typeof sharedConstants.DATALAKE.PARTNER_NAME.fabric.name;
}

export interface NotificationPreference {
  notification_preference_id?: string;
  contact_emails: string;
  notify_on_sync_errors?: boolean;
  notify_on_row_errors?: boolean;
  notify_on_success?: boolean;
  notify_on_empty_syncs?: boolean;
  is_active?: boolean;
  error_threshold?: number;
}

export type CdiImportSync =
  | SnowflakeImportSync
  | RedshiftImportSync
  | BigQueryImportSync
  | DatabricksImportSync
  | S3ImportSync
  | FabricImportSync
  | CDISeparateSourceSync;

export type CdiErrorResponse = {
  result: "error";
  response_code: number;
  message: string;
};

export interface SnowflakeImportKey {
  id?: string;
  user: string;
  role: string;
  region?: string;
  snowflake_account: string;
  rsa_public_key?: string;
  rsa_public_key_old?: string;
  partner_name: typeof sharedConstants.DATALAKE.PARTNER_NAME.snowflake.name;
  app_group_id?: string;
}

export interface RedshiftImportKey {
  id?: string;
  user: string;
  password: string;
  host: string;
  port?: number;
  is_private_network?: boolean;
  ec2_host?: string;
  ssh_user?: string;
  ssh_port?: number;
  rsa_public_key?: string;
  rsa_public_key_old?: string;
  partner_name: typeof sharedConstants.DATALAKE.PARTNER_NAME.redshift.name;
  app_group_id?: string;
}

export interface BigQueryImportKey {
  id?: string;
  service_account_name: string;
  service_account_creds?: string;
  partner_name: typeof sharedConstants.DATALAKE.PARTNER_NAME.bigquery.name;
  app_group_id?: string;
}

export interface DatabricksImportKey {
  id?: string;
  cred_name: string;
  hostname: string;
  http_path: string;
  access_token: string;
  partner_name: typeof sharedConstants.DATALAKE.PARTNER_NAME.databricks.name;
  app_group_id?: string;
}

export interface S3ImportKey {
  id?: string;
  role_arn: string;
  external_id: string;
  partner_name: typeof sharedConstants.DATALAKE.PARTNER_NAME.s3.name;
  app_group_id?: string;
}

export interface FabricImportKey {
  id?: string;
  tenant_id: string;
  principal_id: string;
  client_secret: string;
  name: string;
  connection_string: string;
  azure_vm?: string;
  ssh_user?: string;
  ssh_port?: number;
  rsa_public_key?: string;
  rsa_public_key_old?: string;
  partner_name: typeof sharedConstants.DATALAKE.PARTNER_NAME.fabric.name;
  app_group_id?: string;
}

export type CdiImportKey =
  | SnowflakeImportKey
  | RedshiftImportKey
  | BigQueryImportKey
  | DatabricksImportKey
  | S3ImportKey
  | FabricImportKey;

export type SnowflakeImportKeyWithId = SnowflakeImportKey & { id: string };
export type RedshiftImportKeyWithId = RedshiftImportKey & { id: string };
export type BigQueryImportKeyWithId = BigQueryImportKey & { id: string };
export type DatabricksImportKeyWithId = DatabricksImportKey & { id: string };
export type S3ImportKeyWithId = S3ImportKey & { id: string };
export type FabricImportKeyWithId = FabricImportKey & { id: string };

export type CdiImportKeyWithId =
  | SnowflakeImportKeyWithId
  | RedshiftImportKeyWithId
  | BigQueryImportKeyWithId
  | DatabricksImportKeyWithId
  | S3ImportKeyWithId
  | FabricImportKeyWithId;

export interface AllCdiImportKey {
  snowflake: SnowflakeImportKeyWithId[];
  redshift: RedshiftImportKeyWithId[];
  bigquery: BigQueryImportKeyWithId[];
  databricks: DatabricksImportKeyWithId[];
  s3: S3ImportKeyWithId[];
  fabric: FabricImportKeyWithId[];
}

export interface SnowflakeImportKeysById {
  [key: string]: SnowflakeImportKey;
}

export interface SnowflakeWarehouse {
  id?: string;
  database: string;
  warehouse: string;
  schema: string;
  table: string;
  security_config?: SnowflakeImportKey;
  existing_creds_id?: string;
}

export interface RedshiftWarehouse {
  id?: string;
  database: string;
  schema: string;
  table: string;
  security_config?: RedshiftImportKey;
  existing_creds_id?: string;
}

export interface BigQueryWarehouse {
  id?: string;
  project: string;
  dataset: string;
  table: string;
  security_config?: BigQueryImportKey;
  existing_creds_id?: string;
}

export interface DatabricksWarehouse {
  id?: string;
  catalog: string;
  schema: string;
  table: string;
  security_config?: DatabricksImportKey;
  existing_creds_id?: string;
}

export interface S3Warehouse {
  id?: string;
  sqs_url: string;
  bucket: string;
  folder?: string;
  region: string;
  security_config?: S3ImportKey;
  existing_creds_id?: string;
}

export interface FabricWarehouse {
  id?: string;
  database: string;
  schema: string;
  table: string;
  security_config?: FabricImportKey;
  existing_creds_id?: string;
}

export interface CdiDataSync {
  job_run_id: string;
  integration_id: string;
  job_status: string;
  job_start_time: string;
  job_finish_time?: string;
  errors?: string;
  rows_read?: number;
  rows_ingested?: number;
  rows_rejected_in_read?: number;
  rows_rejected_in_ingest?: number;
  sync_data_type: SyncDataType;
  s3_file_path?: string;
  finish_sync_timestamp?: string;
  s3_sync_input_files?: string[];
}

export interface CdiImportSyncsResponse {
  import_syncs: CdiImportSync[];
}

export interface CdiImportKeysResponse {
  import_keys: CdiImportKeyWithId[];
}

export interface CreateRedshiftImportSyncParams {
  appGroupId: string;
  importSync: RedshiftImportSync;
}

export interface CreateSnowflakeImportSyncParams {
  appGroupId: string;
  importSync: SnowflakeImportSync;
}

export interface CreateCdiImportSyncParams {
  appGroupId: string;
  importSync: CdiImportSync;
}

export interface DeleteCdiImportSyncParams {
  syncId: string;
  partnerName: PartnerName;
}

export interface UpdateSnowflakeImportSyncStatusParams {
  syncId: string;
  status: "active" | "paused";
  partnerName: PartnerName;
}

export interface ListCdiImportSyncsParams {
  appGroupId: string;
  partnerName: PartnerName;
}

export interface GetCdiImportSyncsParams {
  syncId: string;
}

export interface CdiTestConnectionResponse {
  data_preview: {
    PAYLOAD?: string;
    EXTERNAL_ID?: string;
    ALIAS_NAME?: string;
    ALIAS_LABEL?: string;
    BRAZE_ID?: string;
  }[];
  error?: string;
  status: string;
}

export interface CatalogFields {
  fields: CatalogField[];
}

export interface CatalogField {
  name: string;
  type: string;
  selected?: boolean;
}

export interface CdiKeyRegenerateResult {
  status: string;
}

export interface CdiKeyRegenerateProps {
  keyId: string;
  partnerName: PartnerName;
}

export interface PostSnowflakeImportKeyDeleteProps {
  /** The key to be deleted. */
  keyId: string;
  partnerName: PartnerName;
}

export interface GetSnowflakeImportSyncHistoryProps {
  syncId: string;
  page: number;
  hide_empty: boolean;
}

export interface CdiSyncHistoryResponse {
  num_pages: number;
  results: CdiDataSync[];
}

export interface UpdateCdiImportKeyProps {
  keyId: string;
  partner_name: PartnerName;
  import_key: CdiImportKey;
  key_file?: File;
}

export interface UpdateCdiImportSyncStatusResponse {
  status: "successful" | "conflict";
  message?: string;
}

export function isSnowflakeImportSync(obj: CdiImportSync): obj is SnowflakeImportSync {
  return (
    obj !== undefined &&
    (<SnowflakeImportSync>obj).partner_name === sharedConstants.DATALAKE.PARTNER_NAME.snowflake.name &&
    (<SnowflakeImportSync>obj).data_warehouse !== undefined
  );
}

export function isRedshiftImportSync(obj: CdiImportSync): obj is RedshiftImportSync {
  return (
    obj !== undefined &&
    (<RedshiftImportSync>obj).partner_name === sharedConstants.DATALAKE.PARTNER_NAME.redshift.name &&
    (<RedshiftImportSync>obj).data_warehouse !== undefined
  );
}

export function isBigQueryImportSync(obj: CdiImportSync): obj is BigQueryImportSync {
  return (
    obj !== undefined &&
    (<BigQueryImportSync>obj).partner_name === sharedConstants.DATALAKE.PARTNER_NAME.bigquery.name &&
    (<BigQueryImportSync>obj).data_warehouse !== undefined
  );
}

export function isDatabricksImportSync(obj: CdiImportSync): obj is DatabricksImportSync {
  return (
    obj !== undefined &&
    (<DatabricksImportSync>obj).partner_name === sharedConstants.DATALAKE.PARTNER_NAME.databricks.name &&
    (<DatabricksImportSync>obj).data_warehouse !== undefined
  );
}

export function isS3ImportSync(obj: CdiImportSync): obj is S3ImportSync {
  return (
    obj != null &&
    (<S3ImportSync>obj).partner_name === sharedConstants.DATALAKE.PARTNER_NAME.s3.name &&
    (<S3ImportSync>obj).data_warehouse !== undefined
  );
}

export function isFabricImportSync(obj: CdiImportSync): obj is FabricImportSync {
  return (
    obj !== undefined &&
    (<FabricImportSync>obj).partner_name === sharedConstants.DATALAKE.PARTNER_NAME.fabric.name &&
    (<FabricImportSync>obj).data_warehouse !== undefined
  );
}

export function isCDISeparateSourceSync(obj: CdiImportSync): obj is CDISeparateSourceSync {
  return obj !== undefined && (<CDISeparateSourceSync>obj).source_data_warehouse !== undefined;
}

export function isCdiImportSync(obj): obj is CdiImportSync {
  return (
    isSnowflakeImportSync(obj) ||
    isRedshiftImportSync(obj) ||
    isBigQueryImportSync(obj) ||
    isDatabricksImportSync(obj) ||
    isS3ImportSync(obj) ||
    isFabricImportSync(obj) ||
    isCDISeparateSourceSync(obj)
  );
}

export function isSnowflakeSegmentsConfig(obj: CdiSegmentsConfig): obj is SnowflakeSegmentsConfig {
  return (
    obj !== undefined &&
    (<SnowflakeSegmentsConfig>obj).partner_name === sharedConstants.DATALAKE.PARTNER_NAME.snowflake.name &&
    (<SnowflakeSegmentsConfig>obj).security_config !== undefined
  );
}

export function isRedshiftSegmentsConfig(obj: CdiSegmentsConfig): obj is RedshiftSegmentsConfig {
  return (
    obj !== undefined &&
    (<RedshiftSegmentsConfig>obj).partner_name === sharedConstants.DATALAKE.PARTNER_NAME.redshift.name &&
    (<RedshiftSegmentsConfig>obj).security_config !== undefined
  );
}

export function isBigQuerySegmentsConfig(obj: CdiSegmentsConfig): obj is BigQuerySegmentsConfig {
  return (
    obj !== undefined &&
    (<BigQuerySegmentsConfig>obj).partner_name === sharedConstants.DATALAKE.PARTNER_NAME.bigquery.name &&
    (<BigQuerySegmentsConfig>obj).security_config !== undefined
  );
}

export function isDatabricksSegmentsConfig(obj: CdiSegmentsConfig): obj is DatabricksSegmentsConfig {
  return (
    obj !== undefined &&
    (<DatabricksSegmentsConfig>obj).partner_name === sharedConstants.DATALAKE.PARTNER_NAME.databricks.name &&
    (<DatabricksSegmentsConfig>obj).security_config !== undefined
  );
}

export function isFabricSegmentsConfig(obj: CdiSegmentsConfig): obj is FabricSegmentsConfig {
  return (
    obj !== undefined &&
    (<FabricSegmentsConfig>obj).partner_name === sharedConstants.DATALAKE.PARTNER_NAME.fabric.name &&
    (<FabricSegmentsConfig>obj).security_config !== undefined
  );
}

export function isCdiSegmentsConfig(obj): obj is CdiSegmentsConfig {
  return (
    isSnowflakeSegmentsConfig(obj) ||
    isRedshiftSegmentsConfig(obj) ||
    isBigQuerySegmentsConfig(obj) ||
    isDatabricksSegmentsConfig(obj) ||
    isFabricSegmentsConfig(obj)
  );
}

export function isCdiErrorResponse(obj): obj is CdiErrorResponse {
  return (
    Boolean(obj) &&
    obj?.result === "error" &&
    typeof obj?.response_code == "number" &&
    typeof obj?.message === "string"
  );
}

export function isFileStoreIntegration(obj: CdiImportSync | undefined): boolean {
  return !!obj?.partner_name && sharedConstants.DATALAKE.PARTNER_NAME[obj.partner_name]?.source_type === "file";
}

export type CdiInitializeMapDataMap = InitializeMapDataMap;
export type CdiInitializeMapDestinations = InitializeMapDestinations;
export type CdiInitializeMapMappingRule = InitializeMapMappingRule;
export type CdiInitializeMapMappingSummary = InitializeMapMappingSummary;
export type CdiInitializeMapSourceField = InitializeMapSourceField;

export interface PostInitializeCdiMapProps {
  app_group_id: string;
  source_id: string;
  table_name: string;
  integration_type: string;
  catalog_name?: string;
  persisted_rules?: InitializeMapMappingRule[];
}

export interface PostInitializeCdiMapResult {
  data_map: InitializeMapDataMap;
  source: InitializeMapSourceField[];
  destinations: InitializeMapDestinations;
  mapping_summary: InitializeMapMappingSummary;
}
